import { useState, useRef, useCallback } from "react";

import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import { AutoComplete, Input, Tag } from "antd";
import Parse from "parse";
import { useNavigate } from "react-router-dom";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

const RESOURCE_BADGE_COLOR = {
  Identities: "blue",
  "Trusted Issuers": "purple",
  "Claim Topics": "green",
};

// Per-resource colors for the dropdown group headers (.ant-select-item-group),
// giving each section a scannable tinted band + matching bold text.
const RESOURCE_HEADER_STYLE = {
  Identities: { bg: "#eff6ff", fg: "#1d4ed8" },
  "Trusted Issuers": { bg: "#f5f3ff", fg: "#6d28d9" },
  "Claim Topics": { bg: "#f0fdf4", fg: "#15803d" },
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const seqRef = useRef(0); // discard stale responses

  const runSearch = useCallback(
    async (term) => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const res = await Parse.Cloud.run("globalSearch", { term });
        if (seq !== seqRef.current) return; // a newer keystroke won
        setOptions(buildOptions(res?.groups || [], navigate));
      } catch (e) {
        if (seq !== seqRef.current) return;
        console.error("globalSearch failed:", e);
        setOptions([
          {
            label: <span style={{ color: "#dc2626" }}>Search failed. Try again.</span>,
            options: [],
          },
        ]);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    },
    [navigate]
  );

  const onSearch = (text) => {
    setValue(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = text.trim();
    if (term.length < MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  };

  // Selecting an option triggers navigation via the option's onClick; clear input.
  const onSelect = () => {
    setValue("");
    setOptions([]);
  };

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={onSearch}
      onSelect={onSelect}
      style={{ width: 360 }}
      popupMatchSelectWidth={420}
      notFoundContent={value.trim().length >= MIN_CHARS && !loading ? `No matches for "${value.trim()}"` : null}
    >
      <Input allowClear placeholder="Search identities, issuers, rules…" prefix={loading ? <LoadingOutlined /> : <SearchOutlined />} />
    </AutoComplete>
  );
};

// The "Identities" tab key renders as "Approved" in the UI.
const tabLabel = (tab) => (tab === "Identities" ? "Approved" : tab);

// Convert backend groups into AntD AutoComplete grouped options. Identity
// results arrive as one group per tab, so each group's title and "View all"
// count reflect a single destination tab (no cross-tab aggregate confusion).
function buildOptions(groups, navigate) {
  return groups.map((group) => {
    const title = `${group.resource}${group.tab ? ` · ${tabLabel(group.tab)}` : ""}`;
    const groupKey = `${group.resource}${group.tab ? `:${group.tab}` : ""}`;

    const rows = group.items.map((item) => ({
      value: `${groupKey}:${item.id}`,
      label: (
        <div onClick={() => navigate(item.deepLink)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
            {item.sublabel ? (
              <span style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>{item.sublabel}</span>
            ) : null}
          </span>
          <Tag color={RESOURCE_BADGE_COLOR[group.resource] || "default"}>{title}</Tag>
        </div>
      ),
    }));

    if (group.total > group.items.length) {
      rows.push({
        value: `${groupKey}:__viewall`,
        label: (
          <div onClick={() => navigate(group.viewAllLink)} style={{ color: "var(--link-color, #2563eb)", fontSize: 13 }}>
            View all {group.total} in {title} →
          </div>
        ),
      });
    }

    const headerColor = RESOURCE_HEADER_STYLE[group.resource] || { bg: "#f3f4f6", fg: "#374151" };
    return {
      label: (
        // Negative inline margin + restored padding lets the tint span the full
        // row width despite the group item's default horizontal padding.
        <div
          style={{
            margin: "0 -12px",
            padding: "5px 12px",
            background: headerColor.bg,
            color: headerColor.fg,
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {title}
        </div>
      ),
      options: rows,
    };
  });
}

export default GlobalSearch;
